import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

/**
 * School Detail Component
 *
 * Displays detailed information about a specific SMK (Sekolah Menengah Kejuruan)
 * including basic information, documentation status, KJP status, and KIP status.
 *
 * Features:
 * - View complete school information
 * - Upload photos (gedung, ruang kelas, lapangan) to Supabase Storage
 * - Upload KJP file and input KJP number
 * - Upload KIP file and input KIP number & owner name
 * - Progress tracking for each section
 * - Real-time status updates
 *
 * @author AI Agent (OpenClaw)
 * @date February 18, 2026
 */

const SchoolDetail = () => {
  const { id } = useParams();

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Documentation
    foto_gedung: null,
    foto_ruang_kelas: null,
    foto_lapangan: null,

    // KJP
    nomor_kjp: '',
    file_kjp: null,

    // KIP
    nomor_kip: '',
    nama_pemilik_kip: '',
    file_kip: null
  });

  // File preview URLs
  const [previews, setPreviews] = useState({});

  const fetchSchoolDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('sekolah')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setSchool(data);

      // Set initial form data
      setFormData({
        foto_gedung: null,
        foto_ruang_kelas: null,
        foto_lapangan: null,
        nomor_kjp: data.nomor_kjp || '',
        file_kjp: null,
        nomor_kip: data.nomor_kip || '',
        nama_pemilik_kip: data.nama_pemilik_kip || '',
        file_kip: null
      });

      // Set previews for existing files
      setPreviews({
        foto_gedung: data.foto_gedung,
        foto_ruang_kelas: data.foto_ruang_kelas,
        foto_lapangan: data.foto_lapangan,
        file_kjp: data.file_kjp_url,
        file_kip: data.file_kip_url
      });
    } catch (err) {
      console.error('Error fetching school detail:', err);
      setError('Gagal memuat data sekolah.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSchoolDetail();
  }, [fetchSchoolDetail]);

  const handleFileChange = (field, file) => {
    setFormData((prev) => ({ ...prev, [field]: file }));

    // Create preview for image files
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const uploadFile = async (file, path) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${path}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('school-documents')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('school-documents')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSave = async (section) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const updateData = { updated_at: new Date().toISOString() };

      // Handle file uploads
      if (section === 'documentation') {
        if (formData.foto_gedung) {
          updateData.foto_gedung = await uploadFile(formData.foto_gedung, `${id}/gedung`);
        }
        if (formData.foto_ruang_kelas) {
          updateData.foto_ruang_kelas = await uploadFile(formData.foto_ruang_kelas, `${id}/ruang_kelas`);
        }
        if (formData.foto_lapangan) {
          updateData.foto_lapangan = await uploadFile(formData.foto_lapangan, `${id}/lapangan`);
        }

        // Check if all photos are uploaded
        const hasAllPhotos = (updateData.foto_gedung || school.foto_gedung) &&
                            (updateData.foto_ruang_kelas || school.foto_ruang_kelas) &&
                            (updateData.foto_lapangan || school.foto_lapangan);

        if (hasAllPhotos) {
          updateData.status_dokumentasi = 'selesai_upload';
        }
      }

      if (section === 'kjp') {
        updateData.nomor_kjp = formData.nomor_kjp;

        if (formData.file_kjp) {
          updateData.file_kjp_url = await uploadFile(formData.file_kjp, `${id}/kjp`);
        }

        // Check if KJP is complete
        if (formData.nomor_kjp && (formData.file_kjp || school.file_kjp_url)) {
          updateData.status_kjp = 'sudah_upload';
        }
      }

      if (section === 'kip') {
        updateData.nomor_kip = formData.nomor_kip;
        updateData.nama_pemilik_kip = formData.nama_pemilik_kip;

        if (formData.file_kip) {
          updateData.file_kip_url = await uploadFile(formData.file_kip, `${id}/kip`);
        }

        // Check if KIP is complete
        if (formData.nomor_kip && formData.nama_pemilik_kip && (formData.file_kip || school.file_kip_url)) {
          updateData.status_kip = 'sudah_upload';
        }
      }

      // Update school record
      const { error: updateError } = await supabase
        .from('sekolah')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      // Refresh school data
      await fetchSchoolDetail();

      setSuccessMessage('Data berhasil disimpan!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving school data:', err);
      setError('Gagal menyimpan data. Silakan coba lagi.');
    } finally {
      setSaving(false);
    }
  };

  // Progress calculation
  const calculateProgress = () => {
    let completed = 0;
    let total = 3;

    if (school?.status_dokumentasi === 'selesai_upload') completed++;
    if (school?.status_kjp === 'sudah_upload') completed++;
    if (school?.status_kip === 'sudah_upload') completed++;

    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!school && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Sekolah tidak ditemukan.</p>
          <Link to="/schools" className="text-blue-600 hover:text-blue-800">
            Kembali ke Daftar Sekolah
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/schools"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            ← Kembali ke Daftar Sekolah
          </Link>
        </div>

        {/* School Name and Progress */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{school.nama}</h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm">
            <div>
              <span className="text-gray-500">NPSN:</span>
              <span className="ml-2 font-medium text-gray-900">{school.npsn}</span>
            </div>
            <div>
              <span className="text-gray-500">Kecamatan:</span>
              <span className="ml-2 font-medium text-gray-900">{school.kecamatan || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Kota:</span>
              <span className="ml-2 font-medium text-gray-900">{school.kota || '-'}</span>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progress Pengisian Data</span>
              <span className="text-sm font-medium text-blue-600">{calculateProgress()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Documentation Section */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dokumentasi Sekolah</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Photo 1: Gedung */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foto Gedung
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                {previews.foto_gedung ? (
                  <img
                    src={previews.foto_gedung}
                    alt="Foto Gedung"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-100 rounded mb-2 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange('foto_gedung', e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>

            {/* Photo 2: Ruang Kelas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foto Ruang Kelas
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                {previews.foto_ruang_kelas ? (
                  <img
                    src={previews.foto_ruang_kelas}
                    alt="Foto Ruang Kelas"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-100 rounded mb-2 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange('foto_ruang_kelas', e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>

            {/* Photo 3: Lapangan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foto Lapangan
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                {previews.foto_lapangan ? (
                  <img
                    src={previews.foto_lapangan}
                    alt="Foto Lapangan"
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-100 rounded mb-2 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange('foto_lapangan', e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => handleSave('documentation')}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Menyimpan...' : 'Simpan Dokumentasi'}
            </button>
            <span className="ml-3 text-sm text-gray-600">
              Status: {school.status_dokumentasi === 'selesai_upload' ? '✅ Selesai Upload' : '⏳ Belum Upload'}
            </span>
          </div>
        </div>

        {/* KJP Section */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Kartu Jakarta Pintar (KJP)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nomor KJP
              </label>
              <input
                type="text"
                value={formData.nomor_kjp}
                onChange={(e) => handleInputChange('nomor_kjp', e.target.value)}
                placeholder="Masukkan nomor KJP"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File KJP
              </label>
              {previews.file_kjp && (
                <a
                  href={previews.file_kjp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block"
                >
                  Lihat file yang diupload →
                </a>
              )}
              <input
                type="file"
                onChange={(e) => handleFileChange('file_kjp', e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => handleSave('kjp')}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Menyimpan...' : 'Simpan Data KJP'}
            </button>
            <span className="ml-3 text-sm text-gray-600">
              Status: {school.status_kjp === 'sudah_upload' ? '✅ Sudah Upload' : '⏳ Belum Upload'}
            </span>
          </div>
        </div>

        {/* KIP Section */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Kartu Indonesia Pintar (KIP)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nomor KIP
              </label>
              <input
                type="text"
                value={formData.nomor_kip}
                onChange={(e) => handleInputChange('nomor_kip', e.target.value)}
                placeholder="Masukkan nomor KIP"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nama Pemilik KIP
              </label>
              <input
                type="text"
                value={formData.nama_pemilik_kip}
                onChange={(e) => handleInputChange('nama_pemilik_kip', e.target.value)}
                placeholder="Masukkan nama pemilik KIP"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File KIP
              </label>
              {previews.file_kip && (
                <a
                  href={previews.file_kip}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block"
                >
                  Lihat file yang diupload →
                </a>
              )}
              <input
                type="file"
                onChange={(e) => handleFileChange('file_kip', e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => handleSave('kip')}
              disabled={saving}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Menyimpan...' : 'Simpan Data KIP'}
            </button>
            <span className="ml-3 text-sm text-gray-600">
              Status: {school.status_kip === 'sudah_upload' ? '✅ Sudah Upload' : '⏳ Belum Upload'}
            </span>
          </div>
        </div>

        {/* Additional Info */}
        {school.alamat && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Informasi Tambahan</h2>
            <div className="text-sm text-gray-700">
              <p><strong>Alamat:</strong> {school.alamat}</p>
              {school.website && (
                <p className="mt-2">
                  <strong>Website:</strong>{' '}
                  <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    {school.website}
                  </a>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolDetail;
